import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Select,
  Switch,
  Text,
  Textarea,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { pendingPaymentDetail, recruitingDetail, updateCampaign } from "../../api";

const CHANNELS = ["instagram", "youtube", "tiktok", "facebook"];
const REQUIRED_ASSETS = [
  { value: "image", label: "이미지" },
  { value: "video", label: "영상" },
  { value: "text", label: "텍스트" },
  { value: "link", label: "링크" },
  { value: "coupon", label: "쿠폰" },
];

function toDateStr(v) {
  if (!v) return "";
  const s = typeof v === "string" ? v : (v && v.toISOString?.()) || "";
  return s.split("T")[0] || "";
}

function campaignToForm(c) {
  if (!c) return {};
  return {
    type: c.type ?? c.billingModel ?? "",
    brandId: c.brandId ?? "",
    productId: c.productId ?? "",
    title: c.title ?? "",
    description: c.description ?? "",
    goal: c.goal ?? "",
    headcount: c.headcount ?? "",
    industryCategoryIds:
      Array.isArray(c.industryCategoryIds) && c.industryCategoryIds.length > 0
        ? c.industryCategoryIds.join(",")
        : "",
    lifestyleCategoryIds:
      Array.isArray(c.lifestyleCategoryIds) && c.lifestyleCategoryIds.length > 0
        ? c.lifestyleCategoryIds.join(",")
        : "",
    creatorTags:
      Array.isArray(c.creatorTags) && c.creatorTags.length > 0
        ? c.creatorTags.join(",")
        : "",
    recruitStart: toDateStr(c.recruitStart),
    recruitEnd: toDateStr(c.recruitEnd),
    runStart: toDateStr(c.runStart),
    runEnd: toDateStr(c.runEnd),
    country: c.country ?? "",
    language: c.language ?? "",
    gender: c.gender ?? "all",
    ageMin: c.ageMin ?? "",
    ageMax: c.ageMax ?? "",
    channels: Array.isArray(c.channels)
      ? c.channels.map((x) => (typeof x === "string" ? x.toLowerCase() : x))
      : Array.isArray(c.channelsJson)
        ? c.channelsJson.map((x) =>
            typeof x === "string" ? x.toLowerCase() : String(x ?? "").toLowerCase()
          )
        : [],
    requiredAssets: Array.isArray(c.requiredAssets) ? [...c.requiredAssets] : [],
    contentTm: c.contentTm ?? "",
    creativeGuide: c.creativeGuide ?? "",
    referenceLink: c.referenceLink ?? "",
    billingModel: c.billingModel ?? "",
    capReach: c.capReach ?? "",
    allowAnyFollowers: !!c.allowAnyFollowers,
    minFollowers: c.minFollowers ?? "",
  };
}

const initialForm = {
  type: "",
  brandId: "",
  productId: "",
  title: "",
  description: "",
  goal: "",
  headcount: "",
  industryCategoryIds: "",
  lifestyleCategoryIds: "",
  creatorTags: "",
  recruitStart: "",
  recruitEnd: "",
  runStart: "",
  runEnd: "",
  country: "",
  language: "",
  gender: "all",
  ageMin: "",
  ageMax: "",
  channels: [],
  requiredAssets: [],
  contentTm: "",
  creativeGuide: "",
  referenceLink: "",
  billingModel: "",
  capReach: "",
  allowAnyFollowers: false,
  minFollowers: "",
};

export default function CampaignEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [campaign, setCampaign] = useState(null);
  const [form, setForm] = useState(initialForm);

  useEffect(() => {
    if (!id) return;

    const stateCampaign = location.state?.campaign;
    if (stateCampaign) {
      setCampaign(stateCampaign);
      setForm({ ...initialForm, ...campaignToForm(stateCampaign) });
      setLoading(false);
      return;
    }

    (async () => {
      try {
        setLoading(true);
        let res = await pendingPaymentDetail({ id });
        if (res?.data?.campaign) {
          const c = res.data.campaign;
          setCampaign(c);
          setForm({ ...initialForm, ...campaignToForm(c) });
          setLoading(false);
          return;
        }
      } catch (_) {}

      try {
        const res2 = await recruitingDetail({ id });
        if (res2?.data?.campaign) {
          const c = res2.data.campaign;
          setCampaign(c);
          setForm({ ...initialForm, ...campaignToForm(c) });
        }
      } catch (e) {
        console.error("캠페인 조회 실패:", e);
        toast({
          title: "캠페인을 불러올 수 없습니다.",
          description:
            "수정 권한이 없거나 수정 가능한 상태가 아닐 수 있습니다.",
          status: "error",
          duration: 3000,
        });
      } finally {
        setLoading(false);
      }
    })();
  }, [id, location.state, toast]);

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const toggleChannel = (ch) => {
    setForm((prev) => {
      const arr = prev.channels?.includes(ch)
        ? prev.channels.filter((x) => x !== ch)
        : [...(prev.channels || []), ch];
      return { ...prev, channels: arr };
    });
  };

  const toggleRequiredAsset = (asset) => {
    setForm((prev) => {
      const arr = prev.requiredAssets?.includes(asset)
        ? prev.requiredAssets.filter((x) => x !== asset)
        : [...(prev.requiredAssets || []), asset];
      return { ...prev, requiredAssets: arr };
    });
  };

  const buildBody = () => {
    const body = {};
    if (form.type) body.type = form.type;
    if (form.brandId !== "" && form.brandId != null)
      body.brandId = Number(form.brandId) || undefined;
    if (form.productId !== "" && form.productId != null)
      body.productId = Number(form.productId) || undefined;
    if (form.title !== undefined && form.title !== "") body.title = form.title;
    if (form.description !== undefined)
      body.description = form.description;
    if (form.goal) body.goal = form.goal;
    if (form.headcount !== "" && form.headcount != null)
      body.headcount = Number(form.headcount) || undefined;
    if (form.industryCategoryIds !== undefined && form.industryCategoryIds !== "") {
      const ids = form.industryCategoryIds
        .split(",")
        .map((s) => Number(s.trim()))
        .filter((n) => !Number.isNaN(n) && n > 0);
      if (ids.length > 0) body.industryCategoryIds = ids;
    }
    if (form.lifestyleCategoryIds !== undefined && form.lifestyleCategoryIds !== "") {
      const ids = form.lifestyleCategoryIds
        .split(",")
        .map((s) => Number(s.trim()))
        .filter((n) => !Number.isNaN(n) && n > 0);
      if (ids.length > 0) body.lifestyleCategoryIds = ids;
    }
    if (form.creatorTags !== undefined && form.creatorTags !== "") {
      const tags = form.creatorTags
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      if (tags.length > 0) body.creatorTags = tags;
    }
    if (form.recruitStart) body.recruitStart = form.recruitStart;
    if (form.recruitEnd) body.recruitEnd = form.recruitEnd;
    if (form.runStart) body.runStart = form.runStart;
    if (form.runEnd) body.runEnd = form.runEnd;
    if (form.country !== undefined && form.country !== "")
      body.country = form.country;
    if (form.language !== undefined) body.language = form.language;
    if (form.gender) body.gender = form.gender;
    if (form.ageMin !== "" && form.ageMin != null)
      body.ageMin = Number(form.ageMin) >= 0 ? Number(form.ageMin) : undefined;
    if (form.ageMax !== "" && form.ageMax != null)
      body.ageMax = Number(form.ageMax) >= 0 ? Number(form.ageMax) : undefined;
    if (Array.isArray(form.channels) && form.channels.length > 0)
      body.channels = form.channels;
    if (Array.isArray(form.requiredAssets) && form.requiredAssets.length > 0)
      body.requiredAssets = form.requiredAssets;
    if (form.contentTm) body.contentTm = form.contentTm;
    if (form.creativeGuide !== undefined)
      body.creativeGuide = form.creativeGuide;
    if (form.referenceLink !== undefined)
      body.referenceLink = form.referenceLink;
    if (form.billingModel) body.billingModel = form.billingModel;
    if (form.capReach !== "" && form.capReach != null)
      body.capReach = Number(form.capReach) >= 0 ? Number(form.capReach) : undefined;
    if (form.allowAnyFollowers !== undefined)
      body.allowAnyFollowers = form.allowAnyFollowers;
    if (form.minFollowers !== "" && form.minFollowers != null)
      body.minFollowers =
        Number(form.minFollowers) >= 0 ? Number(form.minFollowers) : undefined;
    return body;
  };

  const handleSubmit = async () => {
    const body = buildBody();
    if (Object.keys(body).length === 0) {
      toast({
        title: "변경된 항목이 없습니다.",
        status: "info",
        duration: 2000,
      });
      return;
    }

    try {
      setSaving(true);
      await updateCampaign(Number(id), body);
      toast({
        title: "캠페인 수정 완료",
        status: "success",
        duration: 2000,
      });
      const from = location.state?.source;
      if (from === "pending_payment") {
        navigate(`/suggest/pending_payment/${id}`, { replace: true });
      } else if (from === "recruiting") {
        navigate(`/suggest/recruiting/${id}`, { replace: true });
      } else {
        navigate(-1);
      }
    } catch (e) {
      const code = e?.response?.data?.code;
      const detail = e?.response?.data?.detail || e?.response?.data?.message;
      const msg =
        code === "CAMPAIGN_NOT_EDITABLE"
          ? "현재 상태에서는 수정할 수 없습니다."
          : code === "CAMPAIGN_HAS_APPLICATIONS"
            ? "참여 신청이 있어 수정할 수 없습니다."
            : code === "NOT_FOUND"
              ? "캠페인을 찾을 수 없거나 수정 권한이 없습니다."
              : detail || "캠페인 수정 중 오류가 발생했습니다.";
      toast({
        title: "수정 실패",
        description: msg,
        status: "error",
        duration: 3000,
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Flex h="300px" align="center" justify="center">
        로딩 중...
      </Flex>
    );
  }

  if (!campaign) {
    return (
      <Flex h="300px" align="center" justify="center" color="gray.500">
        데이터를 불러올 수 없습니다.
      </Flex>
    );
  }

  return (
    <Box>
      <Flex mb="24px" justify="space-between" align="center">
        <Text fontSize="xl" fontWeight="bold">
          캠페인 수정
        </Text>
        <Flex gap="12px">
          <Button
            variant="outline"
            onClick={() => navigate(-1)}
            isDisabled={saving}
          >
            취소
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSubmit}
            isLoading={saving}
            loadingText="저장 중"
          >
            저장
          </Button>
        </Flex>
      </Flex>

      <Box
        bg="white"
        borderRadius="12px"
        border="1px solid"
        borderColor="gray.200"
        p="24px"
        maxW="720px"
      >
        <VStack align="stretch" spacing="20px">
          <FormControl>
            <FormLabel>캠페인 유형 (type)</FormLabel>
            <Select
              value={form.type}
              onChange={(e) => updateField("type", e.target.value)}
              placeholder="선택"
            >
              <option value="CPUC">CPUC</option>
              <option value="CPUR">CPUR</option>
            </Select>
          </FormControl>

          <Flex gap="16px" flexWrap="wrap">
            <FormControl flex="1" minW="120px">
              <FormLabel>브랜드 ID (brandId)</FormLabel>
              <Input
                type="number"
                min={1}
                value={form.brandId}
                onChange={(e) => updateField("brandId", e.target.value)}
                placeholder="brandId"
              />
            </FormControl>
            <FormControl flex="1" minW="120px">
              <FormLabel>제품 ID (productId)</FormLabel>
              <Input
                type="number"
                value={form.productId}
                onChange={(e) => updateField("productId", e.target.value)}
                placeholder="productId"
              />
            </FormControl>
          </Flex>

          <FormControl>
            <FormLabel>캠페인명 (title)</FormLabel>
            <Input
              value={form.title}
              onChange={(e) => updateField("title", e.target.value)}
              placeholder="캠페인명"
              maxLength={200}
            />
          </FormControl>

          <FormControl>
            <FormLabel>캠페인 설명 (description)</FormLabel>
            <Textarea
              value={form.description}
              onChange={(e) => updateField("description", e.target.value)}
              placeholder="설명"
              rows={4}
              maxLength={5000}
            />
          </FormControl>

          <FormControl>
            <FormLabel>목표 (goal)</FormLabel>
            <Select
              value={form.goal}
              onChange={(e) => updateField("goal", e.target.value)}
              placeholder="선택"
            >
              <option value="traffic">traffic</option>
              <option value="awareness">awareness</option>
              <option value="conversion">conversion</option>
            </Select>
          </FormControl>

          <FormControl>
            <FormLabel>인원 (headcount)</FormLabel>
            <Input
              type="number"
              min={1}
              value={form.headcount}
              onChange={(e) => updateField("headcount", e.target.value)}
              placeholder="인원"
            />
          </FormControl>

          <FormControl>
            <FormLabel>산업 카테고리 ID (쉼표 구분)</FormLabel>
            <Input
              value={form.industryCategoryIds}
              onChange={(e) =>
                updateField("industryCategoryIds", e.target.value)
              }
              placeholder="예: 1, 2, 3"
            />
          </FormControl>

          <FormControl>
            <FormLabel>라이프스타일 카테고리 ID (쉼표 구분)</FormLabel>
            <Input
              value={form.lifestyleCategoryIds}
              onChange={(e) =>
                updateField("lifestyleCategoryIds", e.target.value)
              }
              placeholder="예: 1, 2"
            />
          </FormControl>

          <FormControl>
            <FormLabel>크리에이터 태그 (쉼표 구분)</FormLabel>
            <Input
              value={form.creatorTags}
              onChange={(e) => updateField("creatorTags", e.target.value)}
              placeholder="예: 태그1, 태그2"
            />
          </FormControl>

          <Flex gap="16px" flexWrap="wrap">
            <FormControl flex="1" minW="140px">
              <FormLabel>모집 시작일</FormLabel>
              <Input
                type="date"
                value={form.recruitStart}
                onChange={(e) => updateField("recruitStart", e.target.value)}
              />
            </FormControl>
            <FormControl flex="1" minW="140px">
              <FormLabel>모집 마감일</FormLabel>
              <Input
                type="date"
                value={form.recruitEnd}
                onChange={(e) => updateField("recruitEnd", e.target.value)}
              />
            </FormControl>
          </Flex>

          <Flex gap="16px" flexWrap="wrap">
            <FormControl flex="1" minW="140px">
              <FormLabel>캠페인 시작일</FormLabel>
              <Input
                type="date"
                value={form.runStart}
                onChange={(e) => updateField("runStart", e.target.value)}
              />
            </FormControl>
            <FormControl flex="1" minW="140px">
              <FormLabel>캠페인 종료일</FormLabel>
              <Input
                type="date"
                value={form.runEnd}
                onChange={(e) => updateField("runEnd", e.target.value)}
              />
            </FormControl>
          </Flex>

          <FormControl>
            <FormLabel>국가 (2자리, 예: KR)</FormLabel>
            <Input
              value={form.country}
              onChange={(e) => updateField("country", e.target.value)}
              placeholder="KR"
              maxLength={2}
            />
          </FormControl>

          <FormControl>
            <FormLabel>언어</FormLabel>
            <Input
              value={form.language}
              onChange={(e) => updateField("language", e.target.value)}
              placeholder="ko"
              maxLength={8}
            />
          </FormControl>

          <FormControl>
            <FormLabel>성별 (gender)</FormLabel>
            <Select
              value={form.gender}
              onChange={(e) => updateField("gender", e.target.value)}
            >
              <option value="all">전체 (all)</option>
              <option value="male">남성 (male)</option>
              <option value="female">여성 (female)</option>
            </Select>
          </FormControl>

          <Flex gap="16px" flexWrap="wrap">
            <FormControl flex="1" minW="100px">
              <FormLabel>최소 연령 (ageMin)</FormLabel>
              <Input
                type="number"
                min={0}
                value={form.ageMin}
                onChange={(e) => updateField("ageMin", e.target.value)}
                placeholder="0"
              />
            </FormControl>
            <FormControl flex="1" minW="100px">
              <FormLabel>최대 연령 (ageMax)</FormLabel>
              <Input
                type="number"
                min={0}
                value={form.ageMax}
                onChange={(e) => updateField("ageMax", e.target.value)}
                placeholder="99"
              />
            </FormControl>
          </Flex>

          <FormControl>
            <FormLabel>채널 (channels)</FormLabel>
            <Flex gap="4" flexWrap="wrap">
              {CHANNELS.map((ch) => (
                <Checkbox
                  key={ch}
                  isChecked={form.channels?.includes(ch)}
                  onChange={() => toggleChannel(ch)}
                >
                  {ch}
                </Checkbox>
              ))}
            </Flex>
          </FormControl>

          <FormControl>
            <FormLabel>필수 에셋 (requiredAssets)</FormLabel>
            <Flex gap="4" flexWrap="wrap">
              {REQUIRED_ASSETS.map(({ value, label }) => (
                <Checkbox
                  key={value}
                  isChecked={form.requiredAssets?.includes(value)}
                  onChange={() => toggleRequiredAsset(value)}
                >
                  {label}
                </Checkbox>
              ))}
            </Flex>
          </FormControl>

          <FormControl>
            <FormLabel>콘텐츠 T&M (contentTm)</FormLabel>
            <Select
              value={form.contentTm}
              onChange={(e) => updateField("contentTm", e.target.value)}
              placeholder="선택"
            >
              <option value="emotional">emotional</option>
              <option value="informational">informational</option>
            </Select>
          </FormControl>

          <FormControl>
            <FormLabel>크리에이터 가이드 (creativeGuide)</FormLabel>
            <Textarea
              value={form.creativeGuide}
              onChange={(e) => updateField("creativeGuide", e.target.value)}
              placeholder="가이드"
              rows={4}
            />
          </FormControl>

          <FormControl>
            <FormLabel>레퍼런스 링크 (referenceLink)</FormLabel>
            <Input
              value={form.referenceLink}
              onChange={(e) => updateField("referenceLink", e.target.value)}
              placeholder="https://..."
              maxLength={1000}
            />
          </FormControl>

          <FormControl>
            <FormLabel>과금 모델 (billingModel)</FormLabel>
            <Select
              value={form.billingModel}
              onChange={(e) => updateField("billingModel", e.target.value)}
              placeholder="선택"
            >
              <option value="CPUC">CPUC</option>
              <option value="CPUR">CPUR</option>
            </Select>
          </FormControl>

          <FormControl>
            <FormLabel>캡 도달 (capReach)</FormLabel>
            <Input
              type="number"
              min={0}
              value={form.capReach}
              onChange={(e) => updateField("capReach", e.target.value)}
              placeholder="0"
            />
          </FormControl>

          <FormControl>
            <Flex align="center" gap="3">
              <Switch
                isChecked={form.allowAnyFollowers}
                onChange={(e) =>
                  updateField("allowAnyFollowers", e.target.checked)
                }
              />
              <FormLabel mb="0">품질 수치 확인 없이 참여 허용 (allowAnyFollowers)</FormLabel>
            </Flex>
          </FormControl>

          <FormControl>
            <FormLabel>최소 팔로워 수 (minFollowers)</FormLabel>
            <Input
              type="number"
              min={0}
              value={form.minFollowers}
              onChange={(e) => updateField("minFollowers", e.target.value)}
              placeholder="0"
            />
          </FormControl>
        </VStack>
      </Box>

      <Box mt="16px" p="12px" bg="blue.50" borderRadius="8px">
        <Text fontSize="sm" color="gray.600">
          예치금(total_budget)은 정책상 수정할 수 없습니다. 에셋(assets) 수정은
          파일 업로드 연동이 필요해 현재 폼에서 제외되어 있습니다.
        </Text>
      </Box>
    </Box>
  );
}
